(() => {
  if (window.__cahveQuickViewBooted) return;
  window.__cahveQuickViewBooted = true;

  const PRODUCT_CACHE = new Map();
  // The quick-view-product section is embedded in the header group so it
  // has a real, editable blocks location. Section groups assign it a
  // runtime id like `sections--<group-id>__quick-view` where the group id
  // varies per theme/duplicate, so it can't be hardcoded — read the real
  // id from the marker Liquid renders unconditionally at the top of that
  // section instead.
  const QUICK_VIEW_SECTION_ID =
    document.querySelector('[data-cahve-qv-section-id]')?.dataset.cahveQvSectionId || 'cahve-quick-view-product';
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  let activeQuickView = null;
  const PAGE_LOCK = {
    owner: null,
    scrollY: 0,
    bodyStyleSnapshot: null,
  };

  class CahveQuickView extends HTMLElement {
    connectedCallback() {
      // Portal the modal to <body>. This keeps position: fixed relative to the
      // viewport even when the owning Shopify section uses transform/contain.
      if (this.dataset.cahvePortaled !== 'true' && this.parentElement !== document.body) {
        this.dataset.cahvePortaled = 'true';
        document.body.appendChild(this);
        return;
      }

      if (this.dataset.cahveInitialized === 'true') return;
      this.dataset.cahveInitialized = 'true';

      this.dialog = this.querySelector('[data-cahve-qv-dialog]');
      this.content = this.querySelector('[data-cahve-qv-content]');
      this.backdrop = this.querySelector('[data-cahve-qv-backdrop]');
      this.cursorClose = this.querySelector('[data-cahve-qv-cursor-close]');
      this.closeTimer = null;
      this.requestController = null;
      this.productController = null;
      this.lastTrigger = null;
      this.shouldRestoreFocus = false;
      this.pointerFrame = null;
      this.pointerX = 0;
      this.pointerY = 0;
      this.pointerMedia = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 990px)');
      this.isSubmitting = false;
      this.dialogRect = null;

      this.handleOverlayClick = this.handleOverlayClick.bind(this);
      this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this);
      this.handlePointerMove = this.handlePointerMove.bind(this);
      this.handlePointerLeave = this.handlePointerLeave.bind(this);
      this.handleViewportResize = this.handleViewportResize.bind(this);
      this.handleSectionUnload = this.handleSectionUnload.bind(this);

      this.addEventListener('click', this.handleOverlayClick);
      this.addEventListener('pointermove', this.handlePointerMove, { passive: true });
      this.addEventListener('pointerleave', this.handlePointerLeave, { passive: true });
      window.addEventListener('resize', this.handleViewportResize, { passive: true });
      document.addEventListener('shopify:section:unload', this.handleSectionUnload);
    }

    disconnectedCallback() {
      if (this.dataset.cahveInitialized !== 'true') return;

      if (activeQuickView === this) {
        activeQuickView = null;
        this.unlockPage();
      }

      this.abortProductListeners();
      this.requestController?.abort();
      this.removeEventListener('click', this.handleOverlayClick);
      this.removeEventListener('pointermove', this.handlePointerMove);
      this.removeEventListener('pointerleave', this.handlePointerLeave);
      window.removeEventListener('resize', this.handleViewportResize);
      document.removeEventListener('keydown', this.handleDocumentKeydown);
      document.removeEventListener('shopify:section:unload', this.handleSectionUnload);
      if (this.pointerFrame) cancelAnimationFrame(this.pointerFrame);
    }

    handleOverlayClick(event) {
      if (!this.classList.contains('is-open')) return;

      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const explicitClose = target.closest('[data-cahve-qv-close]');
      const clickedOutsideDialog = this.dialog && !this.dialog.contains(target);

      if (!explicitClose && !clickedOutsideDialog) return;

      event.preventDefault();
      event.stopPropagation();

      // Pointer/touch closing must not return focus to the product trigger,
      // otherwise its :focus-visible state keeps the old Quick View bar open.
      // Keyboard activation of the visible close control still restores focus.
      const keyboardActivation = Boolean(explicitClose && event.detail === 0);
      this.close(keyboardActivation);
    }

    updateDialogRect() {
      this.dialogRect = this.dialog?.getBoundingClientRect() || null;
    }

    handleViewportResize() {
      if (!this.classList.contains('is-open')) return;
      this.updateDialogRect();
    }

    handlePointerMove(event) {
      if (!this.pointerMedia?.matches || !this.classList.contains('is-open')) return;

      const rect = this.dialogRect || this.dialog?.getBoundingClientRect();
      if (!rect) return;

      const isOutsideDialog =
        event.clientX < rect.left ||
        event.clientX > rect.right ||
        event.clientY < rect.top ||
        event.clientY > rect.bottom;

      this.classList.toggle('is-backdrop-pointer', isOutsideDialog);
      if (!isOutsideDialog || !this.cursorClose) return;

      this.pointerX = event.clientX;
      this.pointerY = event.clientY;
      if (this.pointerFrame) return;

      this.pointerFrame = requestAnimationFrame(() => {
        this.pointerFrame = null;
        this.cursorClose.style.left = `${this.pointerX}px`;
        this.cursorClose.style.top = `${this.pointerY}px`;
      });
    }

    handlePointerLeave() {
      this.classList.remove('is-backdrop-pointer');
    }

    lockPage() {
      const body = document.body;

      if (PAGE_LOCK.bodyStyleSnapshot) {
        PAGE_LOCK.owner = this;
        return;
      }

      PAGE_LOCK.owner = this;
      PAGE_LOCK.scrollY = window.scrollY || window.pageYOffset || 0;
      PAGE_LOCK.bodyStyleSnapshot = {
        position: body.style.position,
        top: body.style.top,
        left: body.style.left,
        right: body.style.right,
        width: body.style.width,
        overflow: body.style.overflow,
      };

      document.documentElement.classList.add('cahve-qv-lock');
      body.style.position = 'fixed';
      body.style.top = `-${PAGE_LOCK.scrollY}px`;
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = '100%';
      body.style.overflow = 'hidden';
    }

    unlockPage() {
      if (PAGE_LOCK.owner !== this) return;

      const snapshot = PAGE_LOCK.bodyStyleSnapshot;
      PAGE_LOCK.owner = null;

      if (!snapshot) {
        document.documentElement.classList.remove('cahve-qv-lock');
        return;
      }

      const body = document.body;
      body.style.position = snapshot.position;
      body.style.top = snapshot.top;
      body.style.left = snapshot.left;
      body.style.right = snapshot.right;
      body.style.width = snapshot.width;
      body.style.overflow = snapshot.overflow;

      PAGE_LOCK.bodyStyleSnapshot = null;
      document.documentElement.classList.remove('cahve-qv-lock');
      window.scrollTo(0, PAGE_LOCK.scrollY);
    }

    handleSectionUnload(event) {
      const ownerSection = this.dataset.cahveOwnerSection;
      if (ownerSection && event.detail?.sectionId === ownerSection) {
        if (activeQuickView === this) {
          activeQuickView = null;
          this.unlockPage();
        }
        this.remove();
      }
    }

    handleDocumentKeydown(event) {
      if (!this.classList.contains('is-open')) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close(true);
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = Array.from(this.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
        (element) => !element.hasAttribute('hidden') && element.offsetParent !== null
      );

      if (!focusable.length) {
        event.preventDefault();
        this.dialog?.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    async open(trigger, { restoreFocus = false } = {}) {
      const productUrl = trigger?.dataset.cahveProductUrl;
      if (!productUrl) return;

      if (activeQuickView && activeQuickView !== this) {
        activeQuickView.close(false);
      }

      activeQuickView = this;
      this.lastTrigger = trigger;
      this.shouldRestoreFocus = Boolean(restoreFocus);
      clearTimeout(this.closeTimer);
      this.classList.remove('is-backdrop-pointer');
      this.hidden = false;
      this.setAttribute('aria-hidden', 'false');
      this.lockPage();
      document.addEventListener('keydown', this.handleDocumentKeydown);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.classList.add('is-open');
          this.updateDialogRect();
          this.dialog?.focus({ preventScroll: true });
        });
      });

      await this.loadProduct(productUrl);
      this.updateDialogRect();
    }

    close(restoreFocus = this.shouldRestoreFocus) {
      if (this.hidden) return;

      const triggerToRestore = this.lastTrigger;

      // Pointer/touch closes must never return visual focus to the product
      // trigger underneath the overlay. Hide only that trigger while the
      // pointer is geometrically over its card, then restore it on pointer exit.
      if (!restoreFocus) {
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
        triggerToRestore?.blur();
        this.suppressPointerTrigger(triggerToRestore);
      }

      this.classList.remove('is-open', 'is-backdrop-pointer');
      this.dialogRect = null;
      if (this.pointerFrame) {
        cancelAnimationFrame(this.pointerFrame);
        this.pointerFrame = null;
      }
      this.requestController?.abort();
      this.pauseMedia();
      document.removeEventListener('keydown', this.handleDocumentKeydown);

      this.closeTimer = window.setTimeout(() => {
        this.hidden = true;
        this.setAttribute('aria-hidden', 'true');

        if (activeQuickView === this) {
          activeQuickView = null;
          this.unlockPage();
        }

        this.lastTrigger = null;
        this.shouldRestoreFocus = false;

        if (restoreFocus && triggerToRestore?.isConnected) {
          triggerToRestore.focus({ preventScroll: true });
        } else {
          triggerToRestore?.blur();
        }
      }, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 470);
    }

    suppressPointerTrigger(trigger) {
      if (!(trigger instanceof HTMLElement)) return;
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

      const card = trigger.closest('[data-cahve-fc-card]');
      if (!(card instanceof HTMLElement)) return;

      const rect = card.getBoundingClientRect();
      const x = Number.isFinite(this.pointerX) ? this.pointerX : -1;
      const y = Number.isFinite(this.pointerY) ? this.pointerY : -1;
      const pointerOverCard = x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;

      if (!pointerOverCard) return;

      trigger.style.setProperty('visibility', 'hidden', 'important');
      trigger.style.setProperty('opacity', '0', 'important');
      trigger.style.setProperty('pointer-events', 'none', 'important');

      const clearSuppression = () => {
        trigger.style.removeProperty('visibility');
        trigger.style.removeProperty('opacity');
        trigger.style.removeProperty('pointer-events');
        document.removeEventListener('pointermove', watchPointer);
      };

      const watchPointer = (event) => {
        const liveRect = card.getBoundingClientRect();
        const outside =
          event.clientX < liveRect.left ||
          event.clientX > liveRect.right ||
          event.clientY < liveRect.top ||
          event.clientY > liveRect.bottom;

        if (outside) clearSuppression();
      };

      document.addEventListener('pointermove', watchPointer, { passive: true });
    }

    setLoading() {
      this.abortProductListeners();
      this.content.innerHTML = `
        <div class="cahve-qv__loading" aria-live="polite" aria-busy="true">
          <span class="cahve-qv__loader-line" aria-hidden="true"></span>
          <span>Loading product…</span>
        </div>
      `;
    }

    setError(message = 'Unable to load this product right now.') {
      this.abortProductListeners();
      this.content.innerHTML = `
        <div class="cahve-qv__loading" role="alert">
          <span>${this.escapeHtml(message)}</span>
        </div>
      `;
    }

    async loadProduct(productUrl) {
      this.setLoading();
      this.requestController?.abort();
      this.requestController = new AbortController();

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('section_id', QUICK_VIEW_SECTION_ID);
      const cacheKey = url.toString();

      try {
        let markup = PRODUCT_CACHE.get(cacheKey);

        if (!markup) {
          const response = await fetch(cacheKey, {
            credentials: 'same-origin',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            signal: this.requestController.signal,
          });

          if (!response.ok) {
            throw new Error(`Quick view request failed with status ${response.status}`);
          }

          markup = await response.text();
          PRODUCT_CACHE.set(cacheKey, markup);
        }

        if (this.requestController.signal.aborted) return;

        const parsed = new DOMParser().parseFromString(markup, 'text/html');
        const productContent = parsed.querySelector('[data-cahve-qv-product]');

        if (!productContent) {
          throw new Error('Quick view product markup was not found.');
        }

        this.abortProductListeners();
        const importedContent = document.importNode(productContent, true);
        // The fetched markup is the header-embedded copy, which is always
        // `hidden` by default (see cahve-quick-view-product.liquid) — clear
        // that on this clone so it's actually visible inside the open modal.
        importedContent.hidden = false;
        this.content.replaceChildren(importedContent);
        this.initializeProduct(this.content.querySelector('[data-cahve-qv-product]'));
        this.updateDialogRect();
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('[Cahvé quick view]', error);
        this.setError();
      }
    }

    initializeProduct(root) {
      if (!root) return;

      this.productController = new AbortController();
      const signal = this.productController.signal;
      const variantScript = root.querySelector('[data-cahve-qv-variants]');

      try {
        this.productData = JSON.parse(variantScript?.textContent || '{}');
      } catch (error) {
        console.error('[Cahvé quick view] Invalid variant JSON.', error);
        this.productData = { variants: [] };
      }

      this.productRoot = root;
      this.currencyCode = root.dataset.currencyCode || window.Shopify?.currency?.active || 'USD';
      this.mediaItems = Array.from(root.querySelectorAll('[data-cahve-qv-media-item]'));
      this.mediaThumbs = Array.from(root.querySelectorAll('[data-cahve-qv-media-thumb]'));
      this.currentMediaIndex = Math.max(
        0,
        this.mediaItems.findIndex((item) => item.classList.contains('is-active'))
      );

      root.addEventListener('click', (event) => this.handleProductClick(event), { signal });
      root
        .querySelector('[data-cahve-qv-product-form]')
        ?.addEventListener('submit', (event) => this.handleAddToCart(event), { signal });
      root
        .querySelector('[data-cahve-qv-quantity-input]')
        ?.addEventListener('input', () => this.syncQuantity(), { signal });

      this.updateVariantFromSelections();
    }

    abortProductListeners() {
      this.productController?.abort();
      this.productController = null;
      this.productRoot = null;
      this.productData = null;
      this.mediaItems = [];
      this.mediaThumbs = [];
    }

    handleProductClick(event) {
      const siblingLink = event.target.closest('[data-cahve-qv-sibling-link]');
      if (siblingLink) {
        event.preventDefault();
        // loadProduct() synchronously wipes this content (setLoading()
        // clears it before the fetch even starts) to show a loading state.
        // If this click keeps bubbling, by the time it reaches the overlay's
        // close-on-outside-click handler, `event.target` (this link) has
        // already been removed from the DOM, so `dialog.contains(target)`
        // reads false and the whole modal closes instead of swapping
        // content. Stop it here so the overlay handler never sees it.
        event.stopPropagation();
        const url = siblingLink.dataset.productUrl;
        if (url) this.loadProduct(url);
        return;
      }

      const optionButton = event.target.closest('[data-cahve-qv-option-button]');
      if (optionButton) {
        event.preventDefault();
        this.selectOption(optionButton);
        return;
      }

      if (event.target.closest('[data-cahve-qv-media-prev]')) {
        event.preventDefault();
        this.showMedia(this.currentMediaIndex - 1);
        return;
      }

      if (event.target.closest('[data-cahve-qv-media-next]')) {
        event.preventDefault();
        this.showMedia(this.currentMediaIndex + 1);
        return;
      }

      const thumb = event.target.closest('[data-cahve-qv-media-thumb]');
      if (thumb) {
        event.preventDefault();
        this.showMedia(Number(thumb.dataset.mediaIndex));
        return;
      }

      if (event.target.closest('[data-cahve-qv-quantity-minus]')) {
        event.preventDefault();
        this.changeQuantity(-1);
        return;
      }

      if (event.target.closest('[data-cahve-qv-quantity-plus]')) {
        event.preventDefault();
        this.changeQuantity(1);
        return;
      }

      if (event.target.closest('[data-cahve-qv-buy-now]')) {
        event.preventDefault();
        this.submitProduct({ checkout: true, sourceButton: event.target.closest('[data-cahve-qv-buy-now]') });
      }
    }

    selectOption(button) {
      const fieldset = button.closest('[data-option-position]');
      if (!fieldset) return;

      fieldset.querySelectorAll('[data-cahve-qv-option-button]').forEach((candidate) => {
        const selected = candidate === button;
        candidate.classList.toggle('is-selected', selected);
        candidate.setAttribute('aria-pressed', String(selected));
      });

      const valueLabel = fieldset.querySelector('[data-cahve-qv-option-value]');
      if (valueLabel) valueLabel.textContent = button.dataset.optionValue || button.textContent.trim();

      this.updateVariantFromSelections();
    }

    getSelectedOptions() {
      return Array.from(this.productRoot.querySelectorAll('[data-option-position]'))
        .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
        .map((fieldset) => {
          const selected = fieldset.querySelector('[data-cahve-qv-option-button].is-selected');
          return selected?.dataset.optionValue || '';
        });
    }

    updateVariantFromSelections() {
      const variants = this.productData?.variants || [];
      const selectedOptions = this.getSelectedOptions();
      const variant = variants.find((candidate) => {
        if (!selectedOptions.length) return true;
        return candidate.options.every((option, index) => option === selectedOptions[index]);
      });

      this.currentVariant = variant || null;
      const variantInput = this.productRoot.querySelector('[data-cahve-qv-variant-input]');
      const price = this.productRoot.querySelector('[data-cahve-qv-price]');
      const comparePrice = this.productRoot.querySelector('[data-cahve-qv-compare-price]');
      const availability = this.productRoot.querySelector('[data-cahve-qv-stock]');
      const availabilityText = this.productRoot.querySelector('[data-cahve-qv-stock-text]');
      const addButton = this.productRoot.querySelector('[data-cahve-qv-add]');
      const addLabel = this.productRoot.querySelector('[data-cahve-qv-add-label]');
      const buyButton = this.productRoot.querySelector('[data-cahve-qv-buy-now]');
      const saveBadge = this.productRoot.querySelector('[data-cahve-qv-save-badge]');
      const soldOutBadge = this.productRoot.querySelector('[data-cahve-qv-sold-out-badge]');

      if (!variant) {
        if (variantInput) variantInput.value = '';
        if (availability) availability.classList.add('is-unavailable');
        if (availabilityText) availabilityText.textContent = 'Unavailable';
        if (addButton) addButton.disabled = true;
        if (buyButton) buyButton.disabled = true;
        this.setRollLabelText(addLabel, 'Unavailable');
        return;
      }

      if (variantInput) variantInput.value = variant.id;
      if (price) price.textContent = this.formatMoney(variant.price);

      const showCompare = Number(variant.compareAtPrice) > Number(variant.price);
      if (comparePrice) {
        comparePrice.hidden = !showCompare;
        comparePrice.textContent = showCompare ? this.formatMoney(variant.compareAtPrice) : '';
      }
      if (saveBadge) {
        saveBadge.hidden = !showCompare;
        saveBadge.textContent = showCompare
          ? `Save ${Math.round(((variant.compareAtPrice - variant.price) / variant.compareAtPrice) * 100)}%`
          : '';
      }
      if (soldOutBadge) soldOutBadge.hidden = variant.available;

      if (availability) availability.classList.toggle('is-unavailable', !variant.available);
      if (availabilityText) {
        const threshold = Number(availability?.dataset.threshold || 5);
        availabilityText.textContent = this.getAvailabilityText(variant, threshold);
      }

      if (addButton) addButton.disabled = !variant.available;
      if (buyButton) buyButton.disabled = !variant.available;
      this.setRollLabelText(addLabel, variant.available ? 'Add to cart' : 'Sold out');

      if (variant.featuredMediaId) {
        const mediaIndex = this.mediaItems.findIndex(
          (item) => Number(item.dataset.mediaId) === Number(variant.featuredMediaId)
        );
        if (mediaIndex >= 0) this.showMedia(mediaIndex);
      }
    }

    // A plain `el.textContent = text` would destroy the two-span
    // `.cahve-roll-label__text` / `--clone` structure the hover-roll
    // animation depends on, permanently flattening it to static text after
    // the first update. Update the inner spans instead so the roll effect
    // survives every dynamic label change (variant switch, add-to-cart
    // states, etc).
    setRollLabelText(el, text) {
      if (!el) return;
      const rollSpans = el.querySelectorAll('.cahve-roll-label__text');
      if (rollSpans.length) {
        rollSpans.forEach((span) => (span.textContent = text));
      } else {
        el.textContent = text;
      }
    }

    getAvailabilityText(variant, threshold = 5) {
      if (!variant.available) return 'Out of stock';
      const showUrgency =
        variant.inventoryManagement && Number(variant.inventoryQuantity) > 0 && Number(variant.inventoryQuantity) <= threshold;
      if (showUrgency) return `Only ${variant.inventoryQuantity} left`;
      return 'In stock';
    }

    formatMoney(cents) {
      const amount = Number(cents || 0) / 100;
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
          style: 'currency',
          currency: this.currencyCode,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(amount);
      } catch (_error) {
        return `${this.currencyCode} ${amount.toFixed(2)}`;
      }
    }

    showMedia(index) {
      if (!this.mediaItems.length) return;

      const normalized = (index + this.mediaItems.length) % this.mediaItems.length;
      this.currentMediaIndex = normalized;

      this.mediaItems.forEach((item, itemIndex) => {
        const active = itemIndex === normalized;
        item.hidden = !active;
        item.classList.toggle('is-active', active);

        if (!active) {
          item.querySelectorAll('video').forEach((video) => video.pause());
        }
      });

      this.mediaThumbs.forEach((thumb, thumbIndex) => {
        const active = thumbIndex === normalized;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-current', String(active));
      });
    }

    changeQuantity(delta) {
      const input = this.productRoot.querySelector('[data-cahve-qv-quantity-input]');
      if (!input) return;

      const current = Math.max(1, Number.parseInt(input.value, 10) || 1);
      input.value = String(Math.max(1, current + delta));
      this.syncQuantity();
    }

    syncQuantity() {
      const input = this.productRoot.querySelector('[data-cahve-qv-quantity-input]');
      const formQuantity = this.productRoot.querySelector('[data-cahve-qv-form-quantity]');
      if (!input || !formQuantity) return;

      const value = Math.max(1, Number.parseInt(input.value, 10) || 1);
      input.value = String(value);
      formQuantity.value = String(value);
    }

    handleAddToCart(event) {
      event.preventDefault();
      this.submitProduct({ checkout: false, sourceButton: event.submitter || event.target.querySelector('[data-cahve-qv-add]') });
    }

    async submitProduct({ checkout, sourceButton }) {
      const form = this.productRoot?.querySelector('[data-cahve-qv-product-form]');
      const addButton = this.productRoot?.querySelector('[data-cahve-qv-add]');
      const addLabel = this.productRoot?.querySelector('[data-cahve-qv-add-label]');
      const buyButton = this.productRoot?.querySelector('[data-cahve-qv-buy-now]');
      const actionButton = sourceButton || (checkout ? buyButton : addButton);

      if (!form || !actionButton || !this.currentVariant?.available || this.isSubmitting) return;

      this.isSubmitting = true;
      this.clearFormError();
      this.syncQuantity();
      actionButton.classList.add('is-loading');
      actionButton.setAttribute('aria-busy', 'true');
      if (addButton) addButton.disabled = true;
      if (buyButton) buyButton.disabled = true;

      const formData = new FormData(form);
      const cartDrawer = document.querySelector('cart-drawer');
      const cartNotification = document.querySelector('cart-notification');
      const cartRenderer = cartDrawer || cartNotification;
      let addedSuccessfully = false;

      if (!checkout && cartRenderer?.getSectionsToRender) {
        formData.append(
          'sections',
          cartRenderer
            .getSectionsToRender()
            .map((section) => section.id)
            .join(',')
        );
        formData.append('sections_url', window.location.pathname);
      }

      try {
        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: formData,
        });

        const result = await response.json();
        if (!response.ok || result.status) {
          throw new Error(result.description || result.message || 'Unable to add this item to the cart.');
        }

        addedSuccessfully = true;

        if (checkout) {
          window.location.assign('/checkout');
          return;
        }

        this.setRollLabelText(addLabel, 'Added');
        actionButton.classList.remove('is-loading');
        actionButton.removeAttribute('aria-busy');

        if (cartRenderer?.renderContents && result.sections) {
          cartRenderer.renderContents(result);
          requestAnimationFrame(() => this.close(false));
        } else {
          document.dispatchEvent(
            new CustomEvent('cahve:cart-updated', {
              detail: { item: result, source: 'quick-view' },
            })
          );

          window.setTimeout(() => {
            if (addLabel && this.currentVariant?.available) this.setRollLabelText(addLabel, 'Add to cart');
          }, 1200);
        }
      } catch (error) {
        console.error('[Cahvé quick view]', error);
        this.showFormError(error.message);
      } finally {
        this.isSubmitting = false;
        actionButton.classList.remove('is-loading');
        actionButton.removeAttribute('aria-busy');

        if (addButton) addButton.disabled = !this.currentVariant?.available;
        if (buyButton) buyButton.disabled = !this.currentVariant?.available;

        if (!addedSuccessfully && addLabel && this.currentVariant?.available) {
          this.setRollLabelText(addLabel, 'Add to cart');
        }
      }
    }

    showFormError(message) {
      const form = this.productRoot?.querySelector('[data-cahve-qv-product-form]');
      if (!form) return;

      const error = document.createElement('p');
      error.className = 'cahve-qv-product__error';
      error.setAttribute('role', 'alert');
      error.textContent = message;
      form.prepend(error);
    }

    clearFormError() {
      this.productRoot?.querySelector('.cahve-qv-product__error')?.remove();
    }

    pauseMedia() {
      this.querySelectorAll('video').forEach((video) => video.pause());
    }

    escapeHtml(value) {
      const element = document.createElement('div');
      element.textContent = value;
      return element.innerHTML;
    }
  }

  if (!customElements.get('cahve-quick-view')) {
    customElements.define('cahve-quick-view', CahveQuickView);
  }

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-cahve-quick-view-trigger]');
    if (!trigger) return;

    const targetId = trigger.dataset.cahveQuickViewTarget;
    const quickView = targetId ? document.getElementById(targetId) : null;
    if (!(quickView instanceof CahveQuickView)) return;

    event.preventDefault();

    // Native keyboard-generated clicks report detail === 0. Only those opens
    // should restore focus to the trigger when the dialog closes.
    quickView.open(trigger, { restoreFocus: event.detail === 0 });
  });
})();
